/**
 * Product Section ERL Style - OpenSpaces Labs
 * Handles:
 * - Thumbnail navigation
 * - Gallery scroll sync
 * - Mobile slider
 * - Variant selection (multi-option support)
 * - Cart drawer integration
 */

(function() {
  'use strict';

  const ErlProduct = {
    product: null,
    selectedOptions: [],

    init: function() {
      this.product = this.getProductData();
      if (!this.product) return;

      // Initialize selected options from product data
      this.selectedOptions = this.product.options.map(() => null);
      
      // Set initial variant
      const initialVariant = this.product.variants.find(v => v.available) || this.product.variants[0];
      if (initialVariant) {
        initialVariant.options.forEach((opt, i) => {
          this.selectedOptions[i] = opt;
        });
      }

      this.initDesktopGallery();
      this.initMobileSlider();
      this.initVariantSelection();
      this.initAddToCart();
      this.initThumbnailVisibility();
      
      // Set initial state
      if (initialVariant) {
        this.updateUI(initialVariant);
        this.selectInitialOptions(initialVariant);
      }
    },

    // ==========================================
    // THUMBNAIL VISIBILITY - Hide when footer comes into view using IntersectionObserver
    // ==========================================
    initThumbnailVisibility: function() {
      const thumbnails = document.querySelector('.erl-thumbnails');
      const productSection = document.querySelector('.erl-product-page');
      
      if (!thumbnails || !productSection) return;
      
      // Find footer element to use as boundary (Plain Jane theme uses .other-footer)
      const footer = document.querySelector('.other-footer') || document.querySelector('footer') || document.querySelector('.footer');
      
      if (!footer) {
        console.log('ERL Thumbnails - No footer found, thumbnails will stay visible');
        return;
      }
      
      console.log('ERL Thumbnails - Using IntersectionObserver on:', footer.className);
      
      // Use IntersectionObserver to detect when footer enters viewport
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // Footer is visible - hide thumbnails
            console.log('ERL Thumbnails - Footer visible, hiding');
            thumbnails.style.opacity = '0';
            thumbnails.style.pointerEvents = 'none';
          } else {
            // Footer not visible - show thumbnails
            console.log('ERL Thumbnails - Footer not visible, showing');
            thumbnails.style.opacity = '1';
            thumbnails.style.pointerEvents = 'auto';
          }
        });
      }, {
        // Trigger when footer top reaches 40% from top of viewport
        rootMargin: '-40% 0px 0px 0px',
        threshold: 0
      });
      
      observer.observe(footer);
    },

    getProductData: function() {
      const jsonElement = document.querySelector('[data-product-json]');
      if (!jsonElement) return null;
      try {
        return JSON.parse(jsonElement.textContent);
      } catch (e) {
        console.error('Error parsing product data:', e);
        return null;
      }
    },

    // ==========================================
    // DESKTOP GALLERY
    // ==========================================
    initDesktopGallery: function() {
      var galleryScroll = document.querySelector('.erl-gallery-scroll');
      var thumbnails = document.querySelectorAll('.erl-thumbnail');
      var galleryImages = document.querySelectorAll('.erl-gallery-image');

      if (!galleryScroll || thumbnails.length === 0) return;

      // Thumbnail click - scroll page to the target image
      thumbnails.forEach(function(thumb) {
        thumb.addEventListener('click', function() {
          var index = parseInt(thumb.dataset.index, 10);
          var targetImage = galleryImages[index];
          if (targetImage) {
            targetImage.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        });
      });

      // Page scroll - update active thumbnail based on which image is in view
      var scrollTimeout;
      window.addEventListener('scroll', function() {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(function() {
          var viewportMiddle = window.scrollY + (window.innerHeight / 2);
          var activeIndex = 0;

          galleryImages.forEach(function(img, index) {
            var rect = img.getBoundingClientRect();
            var imgTop = rect.top + window.scrollY;
            var imgBottom = imgTop + rect.height;

            if (imgTop <= viewportMiddle && imgBottom > viewportMiddle) {
              activeIndex = index;
            }
          });

          thumbnails.forEach(function(thumb, index) {
            thumb.classList.toggle('active', index === activeIndex);
          });
        }, 50);
      });
    },

    // ==========================================
    // MOBILE SLIDER
    // ==========================================
    initMobileSlider: function() {
      var slider = document.querySelector('.erl-mobile-slider');
      var slidesContainer = document.querySelector('.erl-mobile-slides');
      var slides = document.querySelectorAll('.erl-mobile-slide');
      var dots = document.querySelectorAll('.erl-slider-dot');

      if (!slider || !slidesContainer || slides.length === 0) return;

      var currentIndex = 0;

      var updateDots = function(index) {
        dots.forEach(function(dot, i) {
          dot.classList.toggle('active', i === index);
        });
      };

      /* Native scroll with scroll-snap handles swiping via CSS.
         JS only syncs the dot indicators on scroll. */
      var ticking = false;
      slidesContainer.addEventListener('scroll', function() {
        if (!ticking) {
          window.requestAnimationFrame(function() {
            var slideWidth = slidesContainer.clientWidth;
            if (slideWidth > 0) {
              currentIndex = Math.round(slidesContainer.scrollLeft / slideWidth);
              updateDots(currentIndex);
            }
            ticking = false;
          });
          ticking = true;
        }
      }, { passive: true });

      /* Dot tap scrolls to the correct slide */
      dots.forEach(function(dot, index) {
        dot.addEventListener('click', function() {
          currentIndex = index;
          slidesContainer.scrollTo({ left: index * slidesContainer.clientWidth, behavior: 'smooth' });
          updateDots(index);
        });
      });
    },

    // ==========================================
    // VARIANT SELECTION
    // ==========================================
    initVariantSelection: function() {
      const optionBtns = document.querySelectorAll('.erl-option-btn');
      const self = this;

      if (!optionBtns.length) return;

      optionBtns.forEach(btn => {
        btn.addEventListener('click', function(e) {
          e.preventDefault();

          // Don't process if unavailable
          if (this.classList.contains('unavailable')) return;

          const optionIndex = parseInt(this.dataset.optionIndex, 10);
          const optionValue = this.dataset.optionValue;

          // Update selected state in ALL groups with the same option index
          // (syncs desktop and mobile layouts)
          document.querySelectorAll('.erl-option-group[data-option-index="' + optionIndex + '"]').forEach(function(group) {
            group.querySelectorAll('.erl-option-btn').forEach(function(b) {
              b.classList.toggle('selected', b.dataset.optionValue === optionValue);
            });
          });

          // Update selected options array
          self.selectedOptions[optionIndex] = optionValue;

          // Find matching variant
          const variant = self.findVariant();

          if (variant) {
            self.updateUI(variant);
            self.updateURL(variant);
          }

          // Update availability for other options
          self.updateOptionAvailability();
        });
      });
    },

    selectInitialOptions: function(variant) {
      // Use data-option-index attribute instead of forEach index
      // so desktop and mobile groups with the same option index both get updated
      variant.options.forEach(function(optionValue, index) {
        var groups = document.querySelectorAll('.erl-option-group[data-option-index="' + index + '"]');
        groups.forEach(function(group) {
          group.querySelectorAll('.erl-option-btn').forEach(function(b) {
            b.classList.remove('selected');
          });
          var btn = group.querySelector('.erl-option-btn[data-option-value="' + optionValue + '"]');
          if (btn) {
            btn.classList.add('selected');
          }
        });
      });

      this.updateOptionAvailability();
    },

    findVariant: function() {
      return this.product.variants.find(v => {
        return v.options.every((opt, i) => {
          return this.selectedOptions[i] === null || opt === this.selectedOptions[i];
        });
      });
    },

    updateUI: function(variant) {
      // Update hidden inputs
      document.querySelectorAll('[data-variant-id], [data-variant-id-mobile]').forEach(input => {
        input.value = variant.id;
      });

      // Update price
      const priceElements = document.querySelectorAll('[data-product-price], [data-product-price-mobile]');
      priceElements.forEach(el => {
        el.textContent = this.formatMoney(variant.price);
      });

      // Update add to cart button
      const addToCartBtns = document.querySelectorAll('.erl-add-to-cart');
      addToCartBtns.forEach(btn => {
        if (variant.available) {
          btn.disabled = false;
          btn.textContent = 'ADD TO BAG';
          btn.classList.remove('sold-out');
        } else {
          btn.disabled = true;
          btn.textContent = 'SOLD OUT';
          btn.classList.add('sold-out');
        }
      });
    },

    updateURL: function(variant) {
      if (history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url.toString());
      }
    },

    updateOptionAvailability: function() {
      var product = this.product;
      var optionGroups = document.querySelectorAll('.erl-option-group');
      var self = this;

      optionGroups.forEach(function(group) {
        // Read the actual option index from the data attribute
        // (not the forEach index, which doubles up for desktop+mobile)
        var optionIndex = parseInt(group.dataset.optionIndex, 10);
        var buttons = group.querySelectorAll('.erl-option-btn');

        buttons.forEach(function(btn) {
          var optionValue = btn.dataset.optionValue;

          // Check if any variant with this option value is available
          // considering the other selected options
          var hasAvailable = product.variants.some(function(v) {
            var matchesOtherOptions = self.selectedOptions.every(function(selectedOpt, i) {
              if (i === optionIndex) return true; // Skip current option
              if (selectedOpt === null) return true; // No selection yet
              return v.options[i] === selectedOpt;
            });

            return matchesOtherOptions && v.options[optionIndex] === optionValue && v.available;
          });

          if (hasAvailable) {
            btn.classList.remove('unavailable');
            btn.disabled = false;
          } else {
            btn.classList.add('unavailable');
            // Don't disable if it's currently selected
            if (!btn.classList.contains('selected')) {
              btn.disabled = true;
            }
          }
        });
      });
    },

    formatMoney: function(cents) {
      // Use Shopify's format if available
      if (window.Shopify && window.Shopify.formatMoney && window.theme && window.theme.settings) {
        return Shopify.formatMoney(cents, window.theme.settings.moneyFormat);
      }
      // Fallback
      const amount = (cents / 100).toFixed(2);
      return '$' + amount + ' USD';
    },

    // ==========================================
    // ADD TO CART
    // ==========================================
    initAddToCart: function() {
      const forms = document.querySelectorAll('.erl-product-form');
      const self = this;

      forms.forEach(form => {
        form.addEventListener('submit', function(e) {
          e.preventDefault();
          
          const submitBtn = form.querySelector('.erl-add-to-cart');
          if (!submitBtn || submitBtn.disabled) return;

          const variantId = form.querySelector('input[name="id"]').value;
          const originalText = submitBtn.textContent;
          
          // Show loading state
          submitBtn.textContent = 'ADDING...';
          submitBtn.disabled = true;

          fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              id: variantId,
              quantity: 1
            })
          })
          .then(response => {
            if (!response.ok) {
              throw new Error('Add to cart failed');
            }
            return response.json();
          })
          .then(item => {
            // Success - update cart count
            self.updateCartCount();
            
            // Check if cart drawer is enabled and open it
            if (typeof window.openCartDrawer === 'function') {
              window.openCartDrawer();
            } else {
              // Show success message briefly then reset
              submitBtn.textContent = 'ADDED!';
              setTimeout(() => {
                submitBtn.textContent = originalText;
                submitBtn.disabled = false;
              }, 1500);
              return;
            }
            
            // Reset button
            submitBtn.textContent = originalText;
            submitBtn.disabled = false;
          })
          .catch(error => {
            console.error('Error adding to cart:', error);
            submitBtn.textContent = 'ERROR';
            setTimeout(() => {
              submitBtn.textContent = originalText;
              submitBtn.disabled = false;
            }, 1500);
          });
        });
      });
    },

    updateCartCount: function() {
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          // Update cart count elements
          const cartCountElements = document.querySelectorAll('.cart-count');
          cartCountElements.forEach(el => {
            el.textContent = cart.item_count;
          });
          
          // Dispatch cart change event for other components
          document.dispatchEvent(new CustomEvent('cart:change', { 
            detail: cart 
          }));
        })
        .catch(error => console.error('Error updating cart count:', error));
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => ErlProduct.init());
  } else {
    ErlProduct.init();
  }
})();
